import React from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Stack,
  SelectChangeEvent,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import type { TaskStatus, TaskPriority, TaskFilters as TaskFiltersType } from '../../types/task';

interface TaskFiltersProps {
  filters: TaskFiltersType;
  onFiltersChange: (filters: TaskFiltersType) => void;
}

const statusOptions: { value: TaskStatus; label: string }[] = [
  { value: 'todo', label: 'To Do' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const priorityOptions: { value: TaskPriority; label: string }[] = [
  { value: 'urgent', label: 'Urgent' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

const sortOptions = [
  { value: 'createdAt', label: 'Created Date' },
  { value: 'dueDate', label: 'Due Date' },
  { value: 'priority', label: 'Priority' },
  { value: 'title', label: 'Title' },
];

export const TaskFilters: React.FC<TaskFiltersProps> = ({
  filters,
  onFiltersChange,
}) => {
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({ ...filters, search: event.target.value, page: 1 });
  };

  const handleStatusChange = (event: SelectChangeEvent<TaskStatus[]>) => {
    const value = event.target.value as TaskStatus[];
    onFiltersChange({ ...filters, status: value, page: 1 });
  };

  const handlePriorityChange = (event: SelectChangeEvent<TaskPriority[]>) => {
    const value = event.target.value as TaskPriority[];
    onFiltersChange({ ...filters, priority: value, page: 1 });
  };

  const handleSortChange = (event: SelectChangeEvent<string>) => {
    onFiltersChange({
      ...filters,
      sortBy: event.target.value as TaskFiltersType['sortBy'],
      page: 1,
    });
  };

  const handleSortOrderChange = (event: SelectChangeEvent<string>) => {
    onFiltersChange({
      ...filters,
      sortOrder: event.target.value as 'asc' | 'desc',
      page: 1,
    });
  };

  const handleClearSearch = () => {
    onFiltersChange({ ...filters, search: '', page: 1 });
  };

  const handleClearAll = () => {
    onFiltersChange({
      page: 1,
      limit: filters.limit,
    });
  };

  const hasActiveFilters =
    filters.search ||
    filters.status?.length ||
    filters.priority?.length ||
    filters.overdue;

  return (
    <Box sx={{ mb: 3 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
        {/* Search */}
        <TextField
          placeholder="Search tasks..."
          size="small"
          value={filters.search || ''}
          onChange={handleSearchChange}
          sx={{ minWidth: 200, flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: filters.search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClearSearch}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        {/* Status Filter */}
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            multiple
            value={filters.status || []}
            onChange={handleStatusChange}
            input={<OutlinedInput label="Status" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip
                    key={value}
                    label={statusOptions.find((o) => o.value === value)?.label}
                    size="small"
                  />
                ))}
              </Box>
            )}
          >
            {statusOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Priority Filter */}
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Priority</InputLabel>
          <Select
            multiple
            value={filters.priority || []}
            onChange={handlePriorityChange}
            input={<OutlinedInput label="Priority" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip
                    key={value}
                    label={priorityOptions.find((o) => o.value === value)?.label}
                    size="small"
                  />
                ))}
              </Box>
            )}
          >
            {priorityOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Sort By */}
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Sort By</InputLabel>
          <Select
            value={filters.sortBy || 'createdAt'}
            onChange={handleSortChange}
            label="Sort By"
          >
            {sortOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Sort Order */}
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>Order</InputLabel>
          <Select
            value={filters.sortOrder || 'desc'}
            onChange={handleSortOrderChange}
            label="Order"
          >
            <MenuItem value="desc">Newest</MenuItem>
            <MenuItem value="asc">Oldest</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {/* Clear Filters */}
      {hasActiveFilters && (
        <Box>
          <Chip
            label="Clear all filters"
            onDelete={handleClearAll}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Box>
      )}
    </Box>
  );
};

export default TaskFilters;
